/**
 * ToolGroupPicker — sdílený komponent pro výběr MCP nástrojů
 * Skupiny podle modulu, group-level toggle, individual select, search.
 *
 * Reuse: chat module, superagent, libovolný consumer MCP nástrojů.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckSquare, ChevronDown, ChevronRight, MinusSquare, Search, Square } from 'lucide-react';

export interface ToolInfo {
  name: string;
  description?: string;
}

export interface ToolGroupPickerProps {
  /** Skupiny nástrojů { moduleName: [ToolInfo, ...] } */
  groups: Record<string, ToolInfo[]>;
  /** Aktuálně vybrané názvy nástrojů */
  selected: Set<string>;
  /** Callback při změně výběru */
  onChange: (next: Set<string>) => void;
  /** Výška scroll oblasti (default 72) */
  maxHeight?: number;
}

type GroupState = 'all' | 'some' | 'none';

function groupState(names: string[], selected: Set<string>): GroupState {
  const n = names.filter((n) => selected.has(n)).length;
  if (n === 0) return 'none';
  if (n === names.length) return 'all';
  return 'some';
}

function GroupCheckbox({ state, onClick }: { state: GroupState; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className='flex-shrink-0 text-gray-500 hover:text-blue-400 transition-colors'
    >
      {state === 'all' && <CheckSquare size={14} className='text-blue-400' />}
      {state === 'some' && <MinusSquare size={14} className='text-amber-400' />}
      {state === 'none' && <Square size={14} />}
    </button>
  );
}

export function ToolGroupPicker(
  { groups, selected, onChange, maxHeight = 72 }: ToolGroupPickerProps,
) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const q = search.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return groups;
    const result: Record<string, ToolInfo[]> = {};
    for (const [grp, tools] of Object.entries(groups)) {
      const match = tools.filter((t) =>
        t.name.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)
      );
      if (match.length > 0) result[grp] = match;
    }
    return result;
  }, [groups, q]);

  const totalTools = Object.values(groups).flat().length;
  const selectedCount = selected.size;

  function toggleGroup(_grp: string, tools: ToolInfo[]) {
    const names = tools.map((t) => t.name);
    const st = groupState(names, selected);
    const next = new Set(selected);
    if (st === 'all') names.forEach((n) => next.delete(n));
    else names.forEach((n) => next.add(n));
    onChange(next);
  }

  function toggleTool(name: string) {
    const next = new Set(selected);
    next.has(name) ? next.delete(name) : next.add(name);
    onChange(next);
  }

  function toggleOpenGroup(grp: string) {
    const next = new Set(open);
    next.has(grp) ? next.delete(grp) : next.add(grp);
    setOpen(next);
  }

  function selectAll() {
    onChange(new Set(Object.values(groups).flat().map((t) => t.name)));
  }
  function deselectAll() {
    onChange(new Set());
  }

  return (
    <div className='space-y-2'>
      {/* Toolbar */}
      <div className='flex items-center gap-2'>
        <div className='relative flex-1'>
          <Search size={11} className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500' />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('tools.searchToolsPlaceholder', 'Hledat nástroje nebo skupinu…')}
            className='w-full pl-7 pr-3 py-1.5 bg-gray-950 border border-gray-800 rounded-lg text-xs text-white focus:outline-none focus:border-blue-500/50'
          />
        </div>
        <span className='text-[10px] text-gray-600 flex-shrink-0'>
          {selectedCount} / {totalTools}
        </span>
        <button
          onClick={selectAll}
          className='text-[10px] text-blue-500 hover:text-blue-300 flex-shrink-0'
        >
          {t('widgets.catAll', 'Vše')}
        </button>
        <button
          onClick={deselectAll}
          className='text-[10px] text-gray-500 hover:text-gray-300 flex-shrink-0'
        >
          {t('tools.noneSelected', 'Žádný')}
        </button>
      </div>

      {/* Groups */}
      <div
        className={`space-y-1 overflow-y-auto pr-0.5`}
        style={{ maxHeight: `${maxHeight * 4}px` }}
      >
        {Object.entries(filtered).map(([grp, tools]) => {
          const names = tools.map((t) => t.name);
          const st = groupState(names, selected);
          const isOpen = open.has(grp) || q.length > 0;
          const selCount = names.filter((n) => selected.has(n)).length;

          return (
            <div
              key={grp}
              className={`rounded-lg border overflow-hidden ${
                st !== 'none'
                  ? 'border-blue-500/20 bg-blue-950/5'
                  : 'border-gray-800/60 bg-gray-900/20'
              }`}
            >
              {/* Group header */}
              <div className='flex items-center gap-2 px-3 py-2'>
                <GroupCheckbox state={st} onClick={() => toggleGroup(grp, tools)} />
                <button
                  className='flex-1 flex items-center gap-2 text-left'
                  onClick={() => toggleOpenGroup(grp)}
                >
                  <span
                    className={`text-xs font-mono font-semibold ${
                      st !== 'none' ? 'text-white' : 'text-gray-400'
                    }`}
                  >
                    {grp}
                  </span>
                  <span className='text-[10px] text-gray-600'>{selCount}/{tools.length}</span>
                  <span className='ml-auto text-gray-600'>
                    {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                  </span>
                </button>
              </div>

              {/* Tools list */}
              {isOpen && (
                <div className='border-t border-gray-800/40 divide-y divide-gray-800/20'>
                  {tools.map((tool) => {
                    const on = selected.has(tool.name);
                    return (
                      <button
                        key={tool.name}
                        onClick={() => toggleTool(tool.name)}
                        className={`w-full flex items-start gap-2.5 px-4 py-1.5 text-left text-xs transition-colors hover:bg-gray-800/30 ${
                          on ? 'text-white' : 'text-gray-500'
                        }`}
                      >
                        {on
                          ? <CheckSquare size={11} className='text-blue-400 mt-0.5 flex-shrink-0' />
                          : <Square size={11} className='mt-0.5 flex-shrink-0' />}
                        <span className='font-mono'>{tool.name}</span>
                        {tool.description && (
                          <span className='text-gray-600 truncate'>{tool.description}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {Object.keys(filtered).length === 0 && (
          <div className='text-xs text-gray-600 text-center py-6'>
            {t('tools.noToolsMatch', 'Žádné nástroje neodpovídají hledání')}
          </div>
        )}
      </div>
    </div>
  );
}
